/**
 * useAllContacts — lazy, session-cached load of EVERY customer contact.
 *
 * Customer contacts live in a per-customer sub-collection
 * (`customers/{customerId}/contacts/{contactId}`), so there's no top-level
 * collection to subscribe to. To make site contacts and "other" contacts
 * searchable in global search, we pull them all in one `collectionGroup`
 * query the first time search needs them, then cache the result for the
 * session (with a short TTL) so re-opening the search modal is instant.
 *
 * This is deliberately NOT a real-time subscription — contacts change rarely
 * and a one-shot read keeps the cost to a single query per session instead of
 * an always-on listener over thousands of documents.
 *
 * Tenant scoping: the query carries `where('tenantId', 'in', union)`. That
 * filter is REQUIRED — the collection-group rule
 * (`match /{path=**}/contacts/{id}`) cannot look up the parent customer, so it
 * isolates on the contact's own `tenantId` and rejects any query that does not
 * constrain it ("rules are not filters"). Legacy contacts written without a
 * `tenantId` are therefore not returned until the contacts tenant backfill
 * stamps them from their parent customer. The filter also needs the
 * COLLECTION_GROUP single-field index on `contacts.tenantId`
 * (firestore.indexes.json `fieldOverrides`).
 *
 * Graceful degradation: if the query fails (`permission-denied`, or
 * `failed-precondition` while that index is still building) we catch it, log
 * once, and return [] so the rest of global search keeps working.
 */

import { useEffect, useState } from 'react';
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { Contact } from '../types/schema';
import { getTenantIdSync, tenantIdReadUnion } from './useTenantId';

/** A contact enriched with the parent customerId resolved from its doc path. */
export interface ContactSearchRecord extends Contact {
  /** Always set — resolved from the parent doc ref, falling back to stored field. */
  customerId: string;
}

// ---------------------------------------------------------------------------
// Module-level cache (shared across all hook consumers for the session)
// ---------------------------------------------------------------------------

let cache: ContactSearchRecord[] | null = null;
let cacheAt = 0;
let inflight: Promise<ContactSearchRecord[]> | null = null;
let permanentlyFailed = false;

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

async function loadAllContacts(): Promise<ContactSearchRecord[]> {
  const union = tenantIdReadUnion(getTenantIdSync());
  // tenantId filter REQUIRED by the contacts collection-group rule — see header.
  const snap = await getDocs(query(collectionGroup(db, 'contacts'), where('tenantId', 'in', union)));
  return snap.docs
    .map((d) => {
      const data = d.data() as Contact;
      // Parent path: customers/{customerId}/contacts/{contactId}
      const customerId = d.ref.parent.parent?.id || data.customerId || '';
      return { ...data, id: d.id, customerId } as ContactSearchRecord;
    })
    .filter((c) => {
      if (!c.customerId) return false;
      // The query already scopes to this tenant; keep the in-memory check as
      // belt-and-braces. (Unstamped legacy contacts never reach here — the
      // `where('tenantId', 'in', …)` excludes them until they are backfilled.)
      const t = (c as { tenantId?: string }).tenantId;
      return !!t && union.includes(t);
    });
}

/** Test/debug escape hatch — forget the cached contacts. */
export function resetAllContactsCache(): void {
  cache = null;
  cacheAt = 0;
  inflight = null;
  permanentlyFailed = false;
}

/**
 * Lazily load all contacts. Pass `enabled = false` to skip the load entirely
 * (e.g. while the search query is empty) so we never read contacts for a user
 * who opens search and types nothing.
 */
export function useAllContacts(enabled: boolean): {
  contacts: ContactSearchRecord[];
  loading: boolean;
} {
  const [contacts, setContacts] = useState<ContactSearchRecord[]>(cache || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || permanentlyFailed) return;

    // Fresh cache hit — serve immediately, no fetch.
    if (cache && Date.now() - cacheAt < CACHE_TTL_MS) {
      setContacts(cache);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const promise = inflight || (inflight = loadAllContacts());
    promise
      .then((res) => {
        cache = res;
        cacheAt = Date.now();
        inflight = null;
        if (!cancelled) {
          setContacts(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        inflight = null;
        // A permission error almost certainly means the collection-group rule
        // hasn't been deployed. Stop retrying for the session and degrade
        // silently — the rest of search is unaffected.
        if (err?.code === 'permission-denied') permanentlyFailed = true;
        console.warn(
          '[useAllContacts] contact search unavailable — are the collectionGroup ' +
            'rule (match /{path=**}/contacts/{id}) and the contacts.tenantId ' +
            'COLLECTION_GROUP index deployed?',
          err,
        );
        if (!cancelled) {
          setContacts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { contacts, loading };
}
